import { RefObject } from "react";
import { inputObjectArray } from "../../pages/demos/celink/FinancialModelCelinkInputs";
import {
  DataColumn,
  DataItem,
  InputItem,
  OutputItem,
  InputSliderTableInterface,
  DataEncoding,
} from "../../services/types/gallus";
import Plot from "react-plotly.js";
import { formatterWrapper } from "../../features/PlotlyFormatter";

export const doubleArrayTableInputHelper = (
  year: number,
  data: (number | null)[][],
  columnNameArray: string[],
  labelArray: string[],
  symbolArray: { first: string; second: string }[],
) => {
  let columnsObject: DataColumn[] = [];

  columnNameArray.map((columnName: string, columnIndex: number) => {
    var dataColumnTMP: DataColumn = { columnTitle: "", data: [] };
    var dataObjectsTMP: DataItem[] = [];
    for (
      let i = columnIndex * labelArray.length;
      i < columnIndex * labelArray.length + labelArray.length;
      i++
    ) {
      dataObjectsTMP.push({
        firstSymbol: symbolArray[columnIndex].first,
        secondSymbol: symbolArray[columnIndex].second,
        label: labelArray[i] ?? null,
        startingValue: data[year - 2024][i],
      });
    }
    dataColumnTMP.columnTitle = columnName;
    dataColumnTMP.data = dataObjectsTMP;
    columnsObject.push(dataColumnTMP);
  });

  return columnsObject;
};

export const singleArrayTableInputHelper = (
  year: number,
  data: (number | null)[],
  columnNameArray: string[],
  labelArray: string[],
  symbolArray: { first: string; second: string }[],
) => {
  var columnsObject: DataColumn[] = [];

  columnNameArray.map((columnName: string, columnIndex: number) => {
    var dataColumnTMP: DataColumn = { columnTitle: "", data: [] };
    var dataObjectsTMP: DataItem[] = [];
    for (
      let i = columnIndex * labelArray.length;
      i < columnIndex * labelArray.length + labelArray.length;
      i++
    ) {
      dataObjectsTMP.push({
        firstSymbol: symbolArray[columnIndex].first,
        secondSymbol: symbolArray[columnIndex].second,
        label: labelArray[i] ?? null,
        startingValue: data[i],
      });
    }
    dataColumnTMP.columnTitle = columnName;
    dataColumnTMP.data = dataObjectsTMP;
    columnsObject.push(dataColumnTMP);
  });

  return columnsObject;
};

/**
 * "Fills" an Input group with values, therefore making it possible to use it in the input part of the spreadsheet API call.
 * @param inputs The selected input array.
 * @param data The data of all the input groups.
 * @returns An input group that can be used as an input.
 */
export const fillInputWithValues = (
  inputs: InputSliderTableInterface[],
  data: { inputName: string; inputData: any[] }[],
): InputItem[] => {
  var inputsWithValues: InputItem[] = [];
  inputs.forEach((inputElement: InputSliderTableInterface) => {
    if (inputElement.enable) {
      let emptyInputsToFill = inputObjectArray.find((o) => {
        if (
          o.input === inputElement.input &&
          o.year === Number(inputElement.year)
        ) {
          return true;
        }
      });
      let valuesToFillIntoInput = data.find((o) => {
        if (o.inputName === inputElement.input) {
          return true;
        }
      });
      if (
        emptyInputsToFill &&
        valuesToFillIntoInput &&
        emptyInputsToFill.inputObject.length ===
          valuesToFillIntoInput.inputData[inputElement.year - 2024].length
      ) {
        emptyInputsToFill.inputObject.forEach((inputToFill, index) => {
          inputsWithValues.push({
            CellIndex: inputToFill.CellIndex,
            RowIndex: inputToFill.RowIndex,
            Value:
              valuesToFillIntoInput!.inputData[inputElement.year - 2024][
                index
              ] ?? 0,
          });
        });
      } else {
        console.log("Input / Data size mismatch.");
        console.log("Inputs:");
        console.log(emptyInputsToFill);
        console.log("data:");
        console.log(valuesToFillIntoInput ?? "Undefined valuesToFillIntoInput");
      }
    }
  });

  return inputsWithValues;
};

/**
 * Helper function used to generate a JSON to be used in the Spreadsheet API. To be used for square input groups (like a table).
 * @param columns Columns to be queried. Accepts Excel nomenclature.
 * @param initialRow The starting row.
 * @param finalRow The ending row.
 * @returns
 */
export const inputGeneratorTwoDimensions = (
  columns: string[],
  initialRow: number,
  finalRow: number,
) => {
  const result = columns.map((indexOfColumn: string) => {
    for (let i = initialRow; i < finalRow + 1; i++) {
      return {
        RowIndex: i,
        CellIndex: indexOfColumn,
        Value: null,
      };
    }
  });

  return result;
};

/**
 * Helper function used to generate a JSON to be used in the Spreadsheet API. To be used for single rows or columns.
 * @param staticRow Boolean flag that represents whether the static element will be the row (true) or columns (false).
 * @param staticValue The static value that represents either the row or the column to be generated.
 * @param initialValue The starting value of the dynamic value.
 * @param finalValue The ending value of the dynamic value.
 * @returns
 */
export const inputGeneratorOneDimension = (
  staticRow: boolean,
  staticValue: number,
  initialValue: number,
  finalValue: number,
) => {
  var result: OutputItem[] = [];

  for (let i = initialValue; i < finalValue + 1; i++) {
    result.push({
      RowIndex: staticRow ? staticValue : i,
      CellIndex: !staticRow ? staticValue : i,
      Value: null,
    });
  }
  return result;
};

export const genericPlot = (
  data: number[],
  xAxisLabels: string[],
  format: DataEncoding,
  title: string,
  ref?: RefObject<Plot>,
) => {
  return (
    <Plot
      ref={ref}
      data={genericPlotDataHelper(data, xAxisLabels, format)}
      layout={{
        //...PlotlyDefaultMargin,
        barmode: "stack",
        title: {
          text: "<b>" + title + "</b>",
          y: 0.95,
          x: 0.05,
        },
        legend: { orientation: "h" },
        yaxis: {
          visible: false,
          showticklabels: false,
        },
        //annotations: {genericPlotAnnotationsHelper(data, xAxisLabels, format)},
        autosize: true,
      }}
      useResizeHandler
      config={{ responsive: true, displayModeBar: false, staticPlot: true }}
      className="w-100 plotly-set-height"
    />
  );
};

const genericPlotDataHelper = (
  data: number[],
  labels: string[],
  format: DataEncoding,
) => {
  var formattedDataArray: string[] = [];
  data.forEach((value) => {
    formattedDataArray.push(formatterWrapper(value.toString(), format));
  });

  return [
    {
      x: labels,
      y: data,
      type: "bar",
      text: formattedDataArray,
      textfont: {
        size: 16,
      },
      textposition: "inside",
      insidetextanchor: "middle",
      hoverinfo: "none",
      marker: {
        color: data.map((value: number) => {
          return value < 0 ? "rgb(168, 50, 50)" : "rgb(50, 80, 168)";
        }),
      },
    },
  ] as any;
};
